/**
 * Self-hosted Proof-of-Work solver — no third-party libraries.
 *
 * Pairs with the backend (fg_api_v2). Two flows use it:
 *   - Reactive (most endpoints): on a missing/invalid token the backend replies 428 with a fresh
 *     `challenge` in the body; the Axios interceptor solves it and retries (see httpClient.ts).
 *   - Proactive (chunked upload): GET /api/pow/challenge?scope=upload returns
 *     `{ enabled, challenge }`; when enabled we {@link solvePow} it up front and send the token in
 *     the `X-PoW` header on the first chunk, so its body is never uploaded twice.
 * Either way the token goes in the `X-PoW` request header on the protected call.
 *
 * The client must find a nonce so that SHA-256(`${challenge}:${nonce}`) has at least
 * `difficulty` leading zero bits — cheap once for a real user, prohibitive for brute force.
 * See the API's README/RATE_LIMITING.md.
 */

export interface PowChallenge {
  algorithm?: string;
  challenge: string;
  difficulty: number;
  expiry: number;
  scope: string;
  signature: string;
}

/** Counts the leading zero bits of a byte array. */
function leadingZeroBits(bytes: Uint8Array): number {
  let count = 0;
  for (const byte of bytes) {
    if (byte === 0) {
      count += 8;
      continue;
    }
    for (let bit = 7; bit >= 0; bit -= 1) {
      if ((byte & (1 << bit)) === 0) {
        count += 1;
      } else {
        return count;
      }
    }
  }
  return count;
}

/**
 * Finds a nonce satisfying the challenge difficulty and returns the `X-PoW` token in the
 * form `challenge.expiry.difficulty.signature.nonce`. Uses the WebCrypto SubtleCrypto digest,
 * so each iteration awaits — keeping the UI responsive while a spinner is shown.
 */
export async function solvePow(challenge: PowChallenge): Promise<string> {
  const encoder = new window.TextEncoder();
  const maxIterations = 1 << 27; // hard guard; the server caps difficulty well below this

  for (let nonce = 0; nonce < maxIterations; nonce += 1) {
    const input = encoder.encode(`${challenge.challenge}:${nonce}`);
    const digest = await window.crypto.subtle.digest('SHA-256', input);
    if (leadingZeroBits(new Uint8Array(digest)) >= challenge.difficulty) {
      return `${challenge.challenge}.${challenge.expiry}.${challenge.difficulty}.${challenge.signature}.${nonce}`;
    }
  }

  throw new Error('PoW: no solution found within the iteration budget');
}
